import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useLearner } from '../../hooks/useLearner';
import { useMe } from '../../hooks/useMe';
import { useMapData } from './hooks/useMapData';
import { useCurrentAtlas } from '../../hooks/useCurrentAtlas';
import { markTourSeen } from '../../api/auth';
import MapAtlasFrame from '../../components/map/MapAtlasFrame';
import MapNode from './MapNode';
import CharacterFigurine from './CharacterFigurine';
import ChallengeDrawer from './ChallengeDrawer';
import NodeContextDrawer from './NodeContextDrawer';
import LearnerSidebar from './LearnerSidebar';
import MapFloatingControls from './MapFloatingControls';
import StoryShipOverlay from './StoryShipOverlay';
import TourModal from '../../components/tour/TourModal';
import audioManager from '../../lib/audioManager';
import { bgmKeys } from '../../config/audioManifest';
import s from './MapPage.module.css';

// Map a biome slug (e.g. 'ocean-kingdom') to its BGM track key
// (e.g. 'biome-02-ocean-kingdom') by matching the slug suffix in the registry —
// the registry stays the single source of truth, no hand-built track strings.
function biomeKeyToTrack(biomeKey) {
  const slug = biomeKey || 'bamboo-forest';
  const match = bgmKeys().find((k) => k.startsWith('biome-') && k.endsWith(slug));
  return match || 'biome-01-bamboo-forest';
}

/**
 * Map home screen — `/` route (ADR-047).
 *
 * Rendering subsystem (BP-MAP-ATLAS-02): a single <MapAtlasFrame> holds the
 * biome image/video AND the interactive overlay, so nodes project onto the
 * painted platforms at every breakpoint. Atlas (visual) data comes from
 * useCurrentAtlas; level/biome/state come from useMapData (unchanged).
 *
 * BP-DASHBOARD-01: chrome is no longer rendered inside <MapAtlasFrame>.
 * LearnerSidebar (desktop) and MapFloatingControls (mobile) live as fixed
 * overlays on top of the atlas frame. BottomNav, MapHUD, and
 * DailyChallengeBadge are removed.
 */
function getNodeState(nodeLevel, currentLevel) {
  if (nodeLevel < currentLevel) return 'completed';
  if (nodeLevel === currentLevel) return 'current';
  return 'locked';
}

function levelFromNodeKey(nodeKey) {
  return Number.parseInt(String(nodeKey).replace('level-', ''), 10);
}

export default function MapPage() {
  const queryClient = useQueryClient();
  const { data: activeLearner } = useLearner();
  const { data: me } = useMe();
  const learnerId = activeLearner?.id;
  const buddyKey = activeLearner?.buddy?.slug;

  // First-use tutorial. The TourModal was orphaned when the dashboard home
  // screen was replaced by the map (commit a39eb06) — re-wired here since the
  // tour is map-contextual. Shows once for learners whose tour_seen_at is null.
  // tourDismissed hides the modal immediately on complete/skip so there is no
  // flash while ['me'] refetches; tourSeenRef guards POST to fire exactly once.
  const [tourDismissed, setTourDismissed] = useState(false);
  const tourSeenRef = useRef(false);

  const handleTourComplete = async () => {
    if (tourSeenRef.current) return;
    tourSeenRef.current = true;
    setTourDismissed(true);
    try {
      await markTourSeen();
    } catch (err) {
      if (import.meta.env.DEV) console.error('markTourSeen failed:', err);
    }
    queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  const { data: mapData, isLoading, error } = useMapData(learnerId);
  const biomeKey = mapData?.biome_key ?? 'bamboo-forest';
  const currentLevel = mapData?.current_level ?? 0;
  // Learners are floored to Level 1 for figurine placement + auto-scroll: the
  // bamboo-forest atlas has no level-0 node, so a brand-new (cached_level 0)
  // learner's buddy figurine would otherwise resolve to a missing node and
  // render null. Node lock states still use the raw currentLevel; cached_level
  // stays 0-indexed in the DB (FP-FIGURINE-LEVEL-FLOOR-01).
  const displayLevel = Math.max(1, currentLevel);

  const { atlas, isLoading: atlasLoading } = useCurrentAtlas(biomeKey);

  // Biome BGM (audio-system.md). Crossfades to the new biome's track on biome
  // change; the manager no-ops gracefully while assets are absent / muted /
  // map-music disabled.
  useEffect(() => {
    audioManager.playBGM(biomeKeyToTrack(biomeKey), 'biome');
  }, [biomeKey]);

  // drawerKind drives mutual-exclusion across all three drawers/overlays.
  const [drawerKind, setDrawerKind] = useState(null); // 'node' | 'challenges' | 'storyship' | null
  const [drawerNode, setDrawerNode] = useState(null);
  const [shakeNodeKey, setShakeNodeKey] = useState(null);

  // Drawer-whoosh (audio-system.md): one soft whoosh on any map drawer/overlay
  // open OR close. drawerKind is the single mutual-exclusion state for the node,
  // challenges, and story-ship drawers, so a transition either side of it covers
  // all three in one place.
  const prevDrawerKindRef = useRef(drawerKind);
  useEffect(() => {
    if (drawerKind !== prevDrawerKindRef.current) {
      prevDrawerKindRef.current = drawerKind;
      audioManager.playSFX('drawer-whoosh');
    }
  }, [drawerKind]);

  const nodes = (atlas?.nodes ?? []).map((n) => ({ ...n, level: levelFromNodeKey(n.node_key) }));

  // Auto-scroll the current level into view (landscape only). See packet history
  // (FP-MAP-VIEWPORT-FILL-AND-EDITOR-DELETE-01) for the math reasoning.
  const currentNode = (atlas?.nodes ?? []).find((n) => n.node_key === `level-${displayLevel}`);
  const currentSrcY = currentNode?.src_y ?? null;
  const nativeW = atlas?.native_width ?? null;
  const nativeH = atlas?.native_height ?? null;
  const didInitialScroll = useRef(false);

  useEffect(() => {
    if (currentSrcY == null || !nativeW || !nativeH) return;
    if (!window.matchMedia('(orientation: landscape)').matches) return;
    const frameHeight = window.innerWidth * (nativeH / nativeW);
    const target = Math.max(0, currentSrcY * frameHeight - window.innerHeight * 0.6);
    const behavior = didInitialScroll.current ? 'smooth' : 'auto';
    const raf = requestAnimationFrame(() => window.scrollTo({ top: target, behavior }));
    didInitialScroll.current = true;
    return () => cancelAnimationFrame(raf);
  }, [currentSrcY, nativeW, nativeH, atlas?.id, currentLevel]);

  const handleNodeTap = (node, state) => {
    Sentry.addBreadcrumb({
      category: 'map',
      message: 'map.node_tap',
      level: 'info',
      data: { node_key: node.node_key, state },
    });
    if (state === 'locked') {
      audioManager.playSFX('node-tap-locked');
      setShakeNodeKey(node.node_key);
      setTimeout(
        () => setShakeNodeKey((prev) => (prev === node.node_key ? null : prev)),
        450
      );
      return;
    }
    audioManager.playSFX('node-tap');
    setDrawerNode({ ...node, _variant: state });
    setDrawerKind('node');
  };

  return (
    <div className={s.page}>
      {atlas && (
        <MapAtlasFrame atlas={atlas}>
          {mapData && (
            <>
              {nodes.map((node) => {
                // Interaction floor (FP-NEW-LEARNER-LEVEL-FLOOR-01): node lock
                // states compare against displayLevel (= max(1, currentLevel)),
                // matching the figurine placement floor. A level-0 learner's
                // level-1 node resolves to 'current' (not 'locked') so its tap
                // opens the drawer; cached_level stays 0 in the DB/economy.
                const state = getNodeState(node.level, displayLevel);
                return (
                  <MapNode
                    key={node.node_key}
                    node={node}
                    state={state}
                    shake={shakeNodeKey === node.node_key}
                    onTap={() => handleNodeTap(node, state)}
                  />
                );
              })}
              <CharacterFigurine
                buddyKey={buddyKey}
                currentNodeKey={`level-${displayLevel}`}
              />
            </>
          )}
        </MapAtlasFrame>
      )}

      {(isLoading || atlasLoading) && <div className={s.loading}>Loading your map…</div>}
      {error && <div className={s.errorState}>Couldn't load the map. Try again.</div>}

      {learnerId && (
        <>
          <LearnerSidebar onOpenStoryShip={() => setDrawerKind('storyship')} />
          <MapFloatingControls
            childId={learnerId}
            onOpenChallenges={() => setDrawerKind('challenges')}
            onOpenStoryShip={() => setDrawerKind('storyship')}
            challengeOpen={drawerKind === 'challenges'}
          />

          <ChallengeDrawer
            childId={learnerId}
            open={drawerKind === 'challenges'}
            onClose={() => setDrawerKind(null)}
          />
          <NodeContextDrawer
            open={drawerKind === 'node'}
            variant={drawerNode?._variant === 'completed' ? 'completed' : 'current'}
            node={drawerNode}
            childId={learnerId}
            onClose={() => setDrawerKind(null)}
            onOpenChallenges={() => setDrawerKind('challenges')}
          />
          <StoryShipOverlay
            open={drawerKind === 'storyship'}
            onClose={() => setDrawerKind(null)}
          />

          {me && !me.tour_seen_at && !tourDismissed && (
            <TourModal
              learnerName={activeLearner?.name}
              buddyAvatar={activeLearner?.buddy?.slug}
              onComplete={handleTourComplete}
              onSkip={handleTourComplete}
            />
          )}
        </>
      )}
    </div>
  );
}
